import Checkbox from '@repo/ui/checkbox'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getPeriods } from '../../../api/periods/get-periods'
import { getTrips } from '../../../api/trips/get-trips'
import {
  ReportStatusEnum,
  reportStatusEnumMapping
} from '../../../lib/types/enums/affected-staff/report-status.enum'
import {
  StaffPaymentStatusEnum,
  staffPaymentStatusEnumMapping
} from '../../../lib/types/enums/affected-staff/staff-payment-status.enum'
import {
  AccomodationCategoryEnum,
  accomodationCategoryEnumMapping
} from '../../../lib/types/enums/product/accomodation-category.enum'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffSalaryFilter } from '../../../lib/types/models/affectedStaff/affected-staff'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

interface Props {
  type: 'simple' | 'advanced'
  filter?: AffectedStaffSalaryFilter
  onFilterChange: (filter: AffectedStaffSalaryFilter) => void
}

export function AffectedStaffsSalaryFilters({ type, filter, onFilterChange }: Props) {
  const { t } = useTranslation()

  function setFilter<K extends keyof AffectedStaffSalaryFilter>(
    key: K,
    value: AffectedStaffSalaryFilter[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas({ version: 'LIGHT' })
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips({ version: 'BASIC' })
  })

  return (
    <div className={'flex flex-col gap-4 mt-2'}>
      <MultiSelectInput
        label={'Pays'}
        variant={'primary'}
        searchable
        options={countries?.data?.map((country) => ({ label: country.name, value: country.code }))}
        onChange={(countries) => setFilter('countries', countries)}
        selectedOptions={filter?.countries?.map((country) => ({
          label: countries?.data?.find((c) => c.code === country)?.name || '',
          value: country
        }))}
      />

      <MultiSelectInput
        label={'Période(s)'}
        variant={'warning'}
        searchable
        options={periods?.data?.map((period) => ({ label: period.name, value: period.id }))}
        onChange={(periods) => setFilter('periods', periods)}
        selectedOptions={filter?.periods?.map((period) => ({
          label: periods?.data?.find((p) => p.id === period)?.name || '',
          value: period
        }))}
      />

      <MultiSelectInput
        label={'Formules'}
        variant={'primary'}
        searchable
        options={formulas?.data?.map((formula) => ({
          label: getTranslatedName(formula),
          value: formula.id
        }))}
        onChange={(formulas) => setFilter('formulas', formulas)}
        selectedOptions={filter?.formulas?.map((formula) => ({
          label: getTranslatedName(formulas?.data?.find((f) => f.id === formula)),
          value: formula
        }))}
      />

      <MultiSelectInput
        label={'Qualifications'}
        variant={'primary'}
        searchable
        options={Object.values(StaffQualificationEnum).map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
        onChange={(qualifications) => setFilter('qualifications', qualifications)}
        selectedOptions={filter?.qualifications?.map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
      />

      <MultiSelectInput
        label={'Postes'}
        variant={'primary'}
        searchable
        options={Object.values(StaffPositionEnum)
          .filter((position) => position !== StaffPositionEnum.AAT)
          .map((position) => ({
            label: t(staffPositionEnumMapping[position]),
            value: position
          }))}
        onChange={(positions) => setFilter('positions', positions)}
        selectedOptions={filter?.positions?.map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
      />

      <div className="grid grid-cols-2 gap-4">
        <DateStringInput
          label={'Départ après le'}
          value={filter?.departureDateFrom}
          onChange={(date) => setFilter('departureDateFrom', date)}
        />
        <DateStringInput
          label={'Départ avant le'}
          value={filter?.departureDateTo}
          onChange={(date) => setFilter('departureDateTo', date)}
        />
        <DateStringInput
          label={'Retour après le'}
          value={filter?.arrivalDateFrom}
          onChange={(date) => setFilter('arrivalDateFrom', date)}
        />
        <DateStringInput
          label={'Retour avant le'}
          value={filter?.arrivalDateTo}
          onChange={(date) => setFilter('arrivalDateTo', date)}
        />
      </div>

      <MultiSelectInput
        label={"Type d'hébergement"}
        variant={'primary'}
        searchable
        options={Object.values(AccomodationCategoryEnum).map((accomodationCategory) => ({
          label: t(accomodationCategoryEnumMapping[accomodationCategory]),
          value: accomodationCategory
        }))}
        onChange={(accomodations) => setFilter('accomodations', accomodations)}
        selectedOptions={filter?.accomodations?.map((accomodationCategory) => ({
          label: t(accomodationCategoryEnumMapping[accomodationCategory]),
          value: accomodationCategory
        }))}
      />

      <MultiSelectInput
        searchable
        label={'Voyage'}
        variant={'primary'}
        options={trips?.data?.map((trip) => ({
          label: TripProperties.getTripLabel(trip),
          value: trip.id
        }))}
        onChange={(trips) => setFilter('trips', trips)}
        selectedOptions={filter?.trips?.map((trip) => ({
          label: TripProperties.getTripLabel(trips?.data?.find((t) => t.id === trip)),
          value: trip
        }))}
      />

      {type === 'advanced' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <DateStringInput
              label={'Affecté après le'}
              value={filter?.affectedAtFrom}
              onChange={(date) => setFilter('affectedAtFrom', date)}
            />
            <DateStringInput
              label={'Affecté avant le'}
              value={filter?.affectedAtTo}
              onChange={(date) => setFilter('affectedAtTo', date)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="label-medium">Sacoche / rapport</Typography>

            <div className="flex flex-row gap-2">
              {Object.values(ReportStatusEnum).map((reportStatus) => (
                <Checkbox
                  key={reportStatus}
                  checked={filter?.endOfStayReportStatus?.includes(reportStatus)}
                  label={t(reportStatusEnumMapping[reportStatus].label)}
                  onChange={(checked) => {
                    setFilter(
                      'endOfStayReportStatus',
                      checked
                        ? [...(filter?.endOfStayReportStatus || []), reportStatus]
                        : filter?.endOfStayReportStatus?.filter((t) => t !== reportStatus)
                    )
                  }}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="label-medium">Statut du virement</Typography>

            <div className="flex flex-row gap-2">
              {Object.values(StaffPaymentStatusEnum).map((paymentStatus) => (
                <Checkbox
                  key={paymentStatus}
                  checked={filter?.paymentStatuses?.includes(paymentStatus)}
                  label={staffPaymentStatusEnumMapping[paymentStatus].state}
                  onChange={(checked) => {
                    setFilter(
                      'paymentStatuses',
                      checked
                        ? [...(filter?.paymentStatuses || []), paymentStatus]
                        : filter?.paymentStatuses?.filter((t) => t !== paymentStatus)
                    )
                  }}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="label-medium">PAS complété</Typography>

            <div className="flex flex-row gap-2">
              <RadioInput
                checked={filter?.pasCompleted === undefined}
                label="Tous"
                onCheck={() => setFilter('pasCompleted', undefined)}
              />
              <RadioInput
                checked={filter?.pasCompleted}
                label="Oui"
                onCheck={() => setFilter('pasCompleted', true)}
              />
              <RadioInput
                checked={filter?.pasCompleted === false}
                label="Non"
                onCheck={() => setFilter('pasCompleted', false)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
